// Unified localStorage facade (WP-90, phase 0).
//
// One entry point for reading/writing localStorage with:
//   - transparent JSON serialization,
//   - optional per-key TTL (lazy invalidation on read + active periodic sweep),
//   - quota-safe writes (delegates to the shared safeSetItem recovery path),
//   - prefix utilities (list / remove / cap by recency) so callers stop hand-rolling loops.
//
// It does NOT own auth tokens (src/services/auth/tokenStorage.js) or the admin-template
// theme/layout keys — those have their own lifecycles. See docs/storage-keys.md.

import {
  safeSetItem,
  getLocalStorageStats,
  registerCleanupTask,
} from '@/services/LocalStorageCleanupService.js';

import { TTL_MARKER, wrap, isEnvelope, isExpired } from './ttl.js';

// Read + JSON.parse a key. Returns `undefined` for a missing key (distinct from a stored
// `null`), the parsed value otherwise, or the raw string if it is not valid JSON.
function readParsed(key) {
  const raw = localStorage.getItem(key);
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

// Read a value. Unwraps the TTL envelope and, if the entry has expired, removes it and
// returns `fallback`. Legacy (non-enveloped) values are returned as-is.
export function get(key, fallback = null) {
  const parsed = readParsed(key);
  if (parsed === undefined) return fallback;
  if (isEnvelope(parsed)) {
    if (isExpired(parsed)) {
      localStorage.removeItem(key);
      return fallback;
    }
    return parsed.value;
  }
  return parsed;
}

// Write a value, wrapped in the TTL envelope. `ttl` is in milliseconds; 0 = never expires.
// Returns false if the write failed even after emergency cleanup (see safeSetItem).
export function set(key, value, { ttl = 0 } = {}) {
  return safeSetItem(key, JSON.stringify(wrap(value, ttl)), 'storage');
}

export function remove(key) {
  localStorage.removeItem(key);
}

const MISS = Symbol('miss');
export function has(key) {
  return get(key, MISS) !== MISS;
}

// List all keys, optionally filtered by prefix.
export function keys(prefix = '') {
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (!prefix || key.startsWith(prefix))) out.push(key);
  }
  return out;
}

export function removeByPrefix(prefix) {
  if (!prefix) return 0;
  const toRemove = keys(prefix);
  toRemove.forEach((key) => localStorage.removeItem(key));
  return toRemove.length;
}

// Keep only the `max` most-recently-written entries under `prefix`; remove the rest.
// Recency comes from the envelope's writtenAt (legacy entries sort oldest → evicted first).
export function capByPrefix(prefix, max) {
  if (!prefix || max < 0) return 0;
  const entries = keys(prefix).map((key) => {
    const parsed = readParsed(key);
    return { key, writtenAt: isEnvelope(parsed) ? parsed.writtenAt : 0 };
  });
  if (entries.length <= max) return 0;
  entries.sort((a, b) => b.writtenAt - a.writtenAt); // newest first
  const toRemove = entries.slice(max);
  toRemove.forEach(({ key }) => localStorage.removeItem(key));
  return toRemove.length;
}

// Remove every expired managed entry. Fast-rejects non-managed keys by a substring check
// before parsing. Registered into the shared periodic maintenance scheduler below.
export function sweep() {
  const now = Date.now();
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const raw = localStorage.getItem(key);
    if (!raw || raw.indexOf(TTL_MARKER) === -1) continue;
    try {
      if (isExpired(JSON.parse(raw), now)) toRemove.push(key);
    } catch {
      // not a JSON envelope — leave it alone
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
  return toRemove.length;
}

export function stats() {
  return getLocalStorageStats();
}

// Per-key breakdown for the dev monitoring page. Reports size and, for managed entries,
// their TTL metadata. Read-only — does not evict anything.
export function inspect() {
  const now = Date.now();
  const entries = [];
  let totalBytes = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const raw = localStorage.getItem(key) || '';
    const sizeBytes = (key.length + raw.length) * 2; // UTF-16
    totalBytes += sizeBytes;

    let managed = false;
    let expiresAt = null;
    let writtenAt = null;
    if (raw.indexOf(TTL_MARKER) !== -1) {
      try {
        const parsed = JSON.parse(raw);
        if (isEnvelope(parsed)) {
          managed = true;
          expiresAt = parsed.expiresAt;
          writtenAt = parsed.writtenAt;
        }
      } catch {
        // not a JSON envelope
      }
    }

    entries.push({
      key,
      sizeBytes,
      managed,
      expiresAt,
      writtenAt,
      expired: expiresAt != null && now >= expiresAt,
    });
  }
  return { totalBytes, count: entries.length, entries };
}

// Hook the TTL sweep into the one shared scheduler (runs every ~5 min + on unload).
registerCleanupTask(sweep);

export const storage = {
  get,
  set,
  remove,
  has,
  keys,
  removeByPrefix,
  capByPrefix,
  sweep,
  stats,
  inspect,
};

export default storage;
